import * as React from 'react';
import { Formik, Form } from 'formik';
import styled from 'styled-components';

import ArchiveSizeEstimate from 'data-lake/components/ArchiveSizeEstimate';
import { Modal, Alert } from 'components/bootstrap';
import { ModalSubmit } from 'components/common';
import useUserDateTime from 'hooks/useUserDateTime';
import type { AbsoluteTimeRange } from 'views/logic/queries/Query';
import type { DataLakeActionFormValues, DataLakeActionValues, FieldFilters, FieldsOperator } from 'data-lake/Types';
import { getRangeValidationErrors, getDefaultValues } from 'data-lake/components/DataLakeActionModals/utils';
import useCurrentDataLakePlugin from 'data-lake/preview/hooks/useCurrentDataLakePlugin';
import validateFieldFilters from 'data-lake/logic/validateFieldFilters';
import useFieldTypes from 'data-lake/hooks/useFieldTypes';

const MAX_FILTERS = 3;

export type Props = {
  calculateEstimatedSize?: boolean;
  modalTitle: string;
  defaultTimeRange?: AbsoluteTimeRange;
  show: boolean;
  onHide: () => void;
  submitButtonText: string;
  children: React.ReactNode;
  onSubmit: (values: DataLakeActionValues) => void;
  hourOnly?: boolean;
  dataLakeId: string;
  streamIds?: Array<string>;
  defaultFieldFilters?: Array<FieldFilters>;
  defaultFieldOperator?: FieldsOperator;
};

const StyledModal = styled(Modal)`
  z-index: 1070;
`;

const DataLakeRetrievalActionForm = ({
  modalTitle,
  calculateEstimatedSize = true,
  defaultTimeRange = null,
  show,
  onSubmit,
  onHide,
  children,
  submitButtonText,
  hourOnly = false,
  dataLakeId,
  streamIds = undefined,
  defaultFieldFilters = [],
  defaultFieldOperator = 'AND',
}: Props) => {
  const { retrievalFormFieldsComponent: RetrievalFormFields } = useCurrentDataLakePlugin();

  const { inMemoryFields } = useFieldTypes();
  const { formatTime } = useUserDateTime();

  const validate = (values: DataLakeActionFormValues) => {
    const {
      nextTimeRange: { to, from },
      inclusionType,
      fieldFilters = [],
      operator,
    } = values;

    let errors: {
      nextTimeRange?: {
        from?: string;
        to?: string;
      };
      inclusionType?: string;
      fieldFilters?: Array<{ field_name?: string; value?: string }>;
    } = getRangeValidationErrors(from, to);

    if (!inclusionType) {
      errors = { ...errors, inclusionType: 'The Include Message field is required.' };
    }

    const fieldFiltersErrors = validateFieldFilters(fieldFilters, operator, inMemoryFields, MAX_FILTERS);

    if (fieldFiltersErrors.length > 0) {
      errors = {
        ...errors,
        fieldFilters: fieldFiltersErrors,
      };
    }

    return errors;
  };

  const handleSubmit = ({
    nextTimeRange: { from, to },
    wipeRestores,
    inclusionType,
    streamIds: streamIdList,
    fieldFilters,
    operator,
  }: DataLakeActionFormValues) => {
    onSubmit({
      from,
      to,
      wipe_restores: wipeRestores,
      inclusion_type: inclusionType,
      data_lake_id: dataLakeId,
      stream_ids: streamIdList,
      field_filters: fieldFilters,
      operator,
    });
  };

  return (
    <StyledModal onHide={() => onHide()} show={show}>
      <Formik
        initialValues={{
          ...getDefaultValues(
            formatTime,
            defaultTimeRange,
            dataLakeId,
            streamIds,
            defaultFieldFilters,
            defaultFieldOperator,
          ),
        }}
        onSubmit={handleSubmit}
        validate={validate}
        validateOnMount>
        {({ isSubmitting, isValidating, isValid, values }) => (
          <Form>
            <Modal.Header>
              <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {children}
              <RetrievalFormFields hourOnly={hourOnly} submitButtonText={submitButtonText} />
              {calculateEstimatedSize && isValid && (
                <ArchiveSizeEstimate
                  estimateParams={{
                    streamIds: values.streamIds,
                    dataLakeId: values.dataLakeId,
                    from: values.nextTimeRange.from,
                    to: values.nextTimeRange.to,
                    inclusionType: values.inclusionType,
                    fieldFilters: values.fieldFilters,
                    operator: values.operator,
                  }}
                />
              )}
              {!calculateEstimatedSize && (
                <Alert bsStyle="warning">
                  It is not possible to estimate the size of data retrieved from third-party sources. Please review and
                  estimate the size from the source data before starting a large job.
                </Alert>
              )}
            </Modal.Body>
            <Modal.Footer>
              <ModalSubmit
                submitButtonText={submitButtonText}
                submitLoadingText="Submitting request..."
                onCancel={() => onHide()}
                disabledSubmit={isValidating || !isValid}
                isSubmitting={isSubmitting}
              />
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </StyledModal>
  );
};

export default DataLakeRetrievalActionForm;
